import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Display preferences: how the site looks, as opposed to what it shows.
 *
 * They live in a context rather than on the page that uses them because the
 * control that sets them sits in the nav, above the router -- and because a
 * preference that only one page can reach is a preference that never grows a
 * second one.
 *
 * Everything here is per-browser and lives in localStorage. None of it is
 * account state, so it works signed-out and does not need a round trip.
 */

/**
 * Thumbnails can be shown, blurred, or hidden.
 *
 * "Blurred" is the middle setting, for reading the feed as headlines without a
 * column of product photography pulling at the eye -- enough of the image
 * survives to know a watch is there, not enough to pick out which one.
 */
export type ThumbMode = 'on' | 'blurred' | 'off';

export const THUMB_MODES: { key: ThumbMode; label: string }[] = [
  { key: 'on', label: 'On' },
  { key: 'blurred', label: 'Blurred' },
  { key: 'off', label: 'Off' },
];

/**
 * The class list for a news thumbnail under the current setting.
 *
 * Shared because the setting now drives two lists -- the news feed and the
 * per-watch "In the news" section -- and a blur spelled differently in each is
 * a bug waiting to happen. 'off' is deliberately not handled here: that mode
 * drops the element rather than restyling it, which is the caller's call to
 * make since the surrounding markup differs.
 */
export function newsThumbClass(thumbs: ThumbMode): string {
  return thumbs === 'blurred' ? 'news-thumb news-thumb-blur' : 'news-thumb';
}

const THUMBS_KEY = 'watchwatch:newsThumbs';

const storedThumbs = (): ThumbMode => {
  try {
    const v = window.localStorage.getItem(THUMBS_KEY);
    return v === 'blurred' || v === 'off' ? v : 'on';
  } catch {
    // Private-mode browsers can throw on localStorage access; the default is
    // a fine answer and not worth failing the page over.
    return 'on';
  }
};

interface SettingsState {
  thumbs: ThumbMode;
  setThumbs: (mode: ThumbMode) => void;
}

const SettingsContext = createContext<SettingsState | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [thumbs, setThumbs] = useState<ThumbMode>(storedThumbs);

  useEffect(() => {
    try {
      window.localStorage.setItem(THUMBS_KEY, thumbs);
    } catch {
      // Not being able to remember the choice is not worth surfacing.
    }
  }, [thumbs]);

  const value = useMemo<SettingsState>(() => ({ thumbs, setThumbs }), [thumbs]);

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsState {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside a <SettingsProvider>');
  return ctx;
}
